import React, { SFC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Button,
    Dialog,
    DialogTitle,
    makeStyles,
    DialogContent,
    DialogActions,
    Grid,
    Typography,
    Checkbox,
    Tooltip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    FormControlLabel,
    Switch,
    Tabs,
    Tab,
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { Form } from 'react-final-form'
import { Location } from '../../../shared/types/location'
import { DialogProgress } from '../../../shared/components/dialogProgress'
import { DialogConfirmButton } from '../../../shared/components/dialogConfirmButton'
import { TabPanel } from '../../../shared/components/tabPanel'
import { fetchMarkets } from '../../../redux/actions/dashboard'
import { config } from '../../../config'
import LoadBalancerEditor from './LoadBalancerEditor'
import { LoadBalancer, DefaultFlexiPlan, FlexiChargingConnectorType } from '../../../shared/types/flexiCharging'
import { Charger } from '../../../shared/types/charger'
import { v4 as uuidv4 } from 'uuid';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { editFlexiCharging, fetchLocationFlexiPlans, updateLocationFlexiPlans } from '../../../redux/actions/locations'


const mainTenantId = config.mainTenantId
const chargeTenantId = config.chargeTenantId

const DEFAULT_UNIVERSAL_PLAN: DefaultFlexiPlan = {
    connectorType: null,
    maxAcPower: 11,
    maxDcPower: 50,
    requestedKwh: 30,
    durationHours: 6,
    overtimeCharging: true,
}
const DEFAULT_AC_PLAN: DefaultFlexiPlan = {
    connectorType: FlexiChargingConnectorType.Ac,
    maxAcPower: 11,
    maxDcPower: null,
    requestedKwh: 30,
    durationHours: 6,
    overtimeCharging: true,
}
const DEFAULT_DC_PLAN: DefaultFlexiPlan = {
    connectorType: FlexiChargingConnectorType.Dc,
    maxAcPower: null,
    maxDcPower: 50,
    requestedKwh: 30,
    durationHours: 6,
    overtimeCharging: true,
}

const AC_POWER_OPTIONS: { value: number; label: string }[] = [
    { value: 3.7, label: '3.7 kW (1F)' },
    { value: 7.4, label: '7.4 kW (1F)' },
    { value: 11, label: '11 kW (3F)' },
    { value: 17, label: '17 kW (3F)' },
    { value: 22, label: '22 kW (3F)' },
]

const useStyles = makeStyles((theme) => ({
    dialog: {

        '& .MuiDialog-paper': {
            width: '650px',
            maxWidth: 'none',
            height: '80vh',
            maxHeight: 'none',
            display: 'flex',
            flexDirection: 'column',
        },
        '& .MuiFilledInput-root': {
            borderRadius: theme.spacing(0.5),
        },
        '& .MuiFilledInput-root::before': {
            border: 0,
        },
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
        },
        '& input[type=number]': {
            '-moz-appearance': 'textfield',
        },
        '& .DraftEditor-editorContainer': {
            maxHeight: 'calc(100vh - 272px)',
        },
    },
    form: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        flex: 1,
    },

    disabledContainer: {
        opacity: 0.5,
        pointerEvents: 'none',
        userSelect: 'none',
    },

    dialogTitle: {
        paddingBottom: theme.spacing(1),
    },

    dialogContent: {
        flexGrow: 1,
        overflowY: 'auto',
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },

    selectContainer: {
        marginBottom: theme.spacing(3),
    },
    dialogActions: {
        padding: theme.spacing(1, 3, 3, 3),
        flexShrink: 0,
    },
    fullWidth: {
        width: '100%',
    },
    checkbox: {
        color: '#6100ED',
        '&$checked': {
            color: '#6100ED',
        },
        '&$disabled': {
            color: '#A0A0A0',
        },
        '&$checked$disabled': {
            color: '#A0A0A0',
        },
        marginBottom: '4px',
        padding: '0 8px 0 0',
    },
    checked: {},
    disabled: {},
    overtimeCheckboxLabel: {
        marginLeft: 0,
    },
}));

interface FlexiChargingSetupDialogProps {
    open: boolean
    onClose(location?: Location): void
    initialModel?: Location
}

const FlexiChargingSetupDialog: SFC<FlexiChargingSetupDialogProps> = ({ open, onClose, initialModel }) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const dispatch = useDispatch()

    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)
    const marketsState = useSelector((state: any) => state.markets)

    const [tabIndex, setTabIndex] = useState<number>(0)
    const [flexiCharging, setFlexiCharging] = useState<boolean>(initialModel?.flexiCharging || false)
    const [preferFlexiCharging, setPreferFlexiCharging] = useState<boolean>(initialModel?.preferFlexiCharging ?? true)
    const [beforeSubmit, setBeforeSubmit] = useState<boolean>(true)
    const [, setTenant] = useState<string>('')
    const [market, setMarket] = useState<string>('')

    const [loadBalancers, setLoadBalancers] = useState<LoadBalancer[]>(initialModel?.loadBalancers || []);
    const [chargers, setChargers] = useState<Charger[]>(initialModel?.chargers || []);

    const [defaultPlanSplit, setDefaultPlanSplit] = useState<boolean>(false)
    const [universalPlan, setUniversalPlan] = useState<DefaultFlexiPlan>(DEFAULT_UNIVERSAL_PLAN)
    const [acPlan, setAcPlan] = useState<DefaultFlexiPlan>(DEFAULT_AC_PLAN)
    const [dcPlan, setDcPlan] = useState<DefaultFlexiPlan>(DEFAULT_DC_PLAN)

    // Default flexi plan - vsetky pouzite polia su povinne (nesmu byt null/prazdne)
    const isPlanValueMissing = (value: number | null | undefined) => value === null || value === undefined
    const universalPlanErrors = {
        maxAcPower: isPlanValueMissing(universalPlan.maxAcPower),
        maxDcPower: isPlanValueMissing(universalPlan.maxDcPower),
        requestedKwh: isPlanValueMissing(universalPlan.requestedKwh),
        durationHours: isPlanValueMissing(universalPlan.durationHours),
    }
    const acPlanErrors = {
        maxAcPower: isPlanValueMissing(acPlan.maxAcPower),
        requestedKwh: isPlanValueMissing(acPlan.requestedKwh),
        durationHours: isPlanValueMissing(acPlan.durationHours),
    }
    const dcPlanErrors = {
        maxDcPower: isPlanValueMissing(dcPlan.maxDcPower),
        requestedKwh: isPlanValueMissing(dcPlan.requestedKwh),
        durationHours: isPlanValueMissing(dcPlan.durationHours),
    }
    const hasPlanValidationErrors = defaultPlanSplit
        ? Object.values(acPlanErrors).some(Boolean) || Object.values(dcPlanErrors).some(Boolean)
        : Object.values(universalPlanErrors).some(Boolean)

    const onDialogClose = (location?: Location) => {
        onClose(location)
    }

    const handleCreateLb = () => {
        const newLb: LoadBalancer = {
            id: uuidv4(),
            locationId: initialModel?.id || '',
            name: 'New LB',
            capacityKw: 0,
            parentLbId: null,
            active: true,
        };
        setLoadBalancers(prev => {
            const newArray = [...prev, newLb];
            return newArray;
        });
        return newLb.id;
    };

    const handleEditLb = (type: "lb" | "charger", id: string, changes: any) => {

        if (type === "lb") {
            // UPDATE LOAD BALANCER
            setLoadBalancers(prev =>
                prev.map(lb =>
                    lb.id === id ? { ...lb, ...changes } : lb
                )
            );
            return;
        }

        if (type === "charger") {
            // UPDATE CHARGER
            setChargers(prev =>
                prev.map(ch =>
                    ch.id === id ? { ...ch, ...changes } : ch
                )
            );
            return;
        }
    };

    const handleDeleteLb = (lbId: string) => {
        // Find all nested children LBs recursively
        const allChildIds = (id: string): string[] => {
            const children = loadBalancers.filter(lb => lb.parentLbId === id);
            return children.reduce<string[]>(
                (acc, child) => [...acc, child.id, ...allChildIds(child.id)],
                []
            );
        };

        const idsToDelete = [lbId, ...allChildIds(lbId)]; // all LBs to remove

        // 1) Remove the LBs
        setLoadBalancers(prev =>
            prev.filter(lb => !idsToDelete.includes(lb.id))
        );

        // 2) Unassign chargers that belonged to deleted LBs
        setChargers(prev =>
            prev.map(c =>
                c.loadBalancerId && idsToDelete.includes(c.loadBalancerId)
                    ? { ...c, loadBalancerId: null }
                    : c
            )
        );
    };

    const handleFlexiChargingChange = () => {
        setFlexiCharging(!flexiCharging)
    }

    const handlePreferFlexiChargingChange = () => {
        setPreferFlexiCharging(!preferFlexiCharging)
    }

    const handleMarketChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setMarket(event.target.value as string)
    }

    const makePlanNumberFieldHandler = (
        setter: React.Dispatch<React.SetStateAction<DefaultFlexiPlan>>,
        field: 'maxAcPower' | 'maxDcPower' | 'requestedKwh' | 'durationHours'
    ) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value
        const value = raw === '' ? null : Number(raw)
        setter(prev => ({ ...prev, [field]: value }))
    }

    const makePlanOvertimeHandler = (setter: React.Dispatch<React.SetStateAction<DefaultFlexiPlan>>) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setter(prev => ({ ...prev, overtimeCharging: event.target.checked }))
        }

    const makePlanAcPowerHandler = (setter: React.Dispatch<React.SetStateAction<DefaultFlexiPlan>>) =>
        (event: React.ChangeEvent<{ value: unknown }>) => {
            setter(prev => ({ ...prev, maxAcPower: Number(event.target.value) }))
        }

    const handleDefaultPlanSplitToggle = (checked: boolean) => {
        if (checked) {
            // spolocny plan -> AC/DC: prevezmeme hodnoty zo spolocneho planu do oboch novych planov
            setAcPlan(prev => ({
                ...prev,
                connectorType: FlexiChargingConnectorType.Ac,
                maxAcPower: universalPlan.maxAcPower,
                maxDcPower: null,
                requestedKwh: universalPlan.requestedKwh,
                durationHours: universalPlan.durationHours,
                overtimeCharging: universalPlan.overtimeCharging,
            }))
            setDcPlan(prev => ({
                ...prev,
                connectorType: FlexiChargingConnectorType.Dc,
                maxAcPower: null,
                maxDcPower: universalPlan.maxDcPower,
                requestedKwh: universalPlan.requestedKwh,
                durationHours: universalPlan.durationHours,
                overtimeCharging: universalPlan.overtimeCharging,
            }))
        } else {
            // AC/DC -> spolocny plan: maxAcPower z AC planu, maxDcPower z DC planu,
            // ostatne (spolocne) hodnoty prevezmeme z AC planu ako nadradeneho
            setUniversalPlan(prev => ({
                ...prev,
                connectorType: null,
                maxAcPower: acPlan.maxAcPower,
                maxDcPower: dcPlan.maxDcPower,
                requestedKwh: acPlan.requestedKwh,
                durationHours: acPlan.durationHours,
                overtimeCharging: acPlan.overtimeCharging,
            }))
        }
        setDefaultPlanSplit(checked)
    }

    const handleSubmit = async () => {
        if (!initialModel?.id) {
            console.error("Location ID is missing");
            return;
        }

        if (hasPlanValidationErrors) {
            return;
        }

        try {
            const result: any = await dispatch(
                editFlexiCharging(
                    initialModel.id, // guaranteed string now
                    chargers,
                    loadBalancers,
                    market,
                    flexiCharging,
                    preferFlexiCharging
                )
            );

            const plansToSave: DefaultFlexiPlan[] = defaultPlanSplit
                ? [
                    { ...acPlan, connectorType: FlexiChargingConnectorType.Ac, maxDcPower: null },
                    { ...dcPlan, connectorType: FlexiChargingConnectorType.Dc, maxAcPower: null },
                ]
                : [{ ...universalPlan, connectorType: null }]

            await dispatch(updateLocationFlexiPlans(initialModel.id, plansToSave));

            if (result) {
                onDialogClose(result);
            }
        } catch (err) {
            console.error(err);
        }
    };

    // load all general data
    useEffect(() => {
        dispatch(fetchMarkets())
    }, [dispatch, tenantId])

    // load existing default flexi plan(y) pre lokaciu
    useEffect(() => {
        if (!open || !initialModel?.id) {
            setDefaultPlanSplit(false);
            setUniversalPlan(DEFAULT_UNIVERSAL_PLAN);
            setAcPlan(DEFAULT_AC_PLAN);
            setDcPlan(DEFAULT_DC_PLAN);
            return;
        }

        (async () => {
            const plans: any = await dispatch(fetchLocationFlexiPlans(initialModel.id));
            const list: DefaultFlexiPlan[] = Array.isArray(plans) ? plans : [];

            const ac = list.find(p => p.connectorType === FlexiChargingConnectorType.Ac);
            const dc = list.find(p => p.connectorType === FlexiChargingConnectorType.Dc);
            const universal = list.find(p => p.connectorType === null || p.connectorType === undefined);

            if (ac && dc) {
                setDefaultPlanSplit(true);
                setAcPlan(ac);
                setDcPlan(dc);
            } else {
                setDefaultPlanSplit(false);
                setUniversalPlan(universal ?? DEFAULT_UNIVERSAL_PLAN);
                // ak nahodou existuje len jeden specificky plan (bez univerzalneho), pouzijeme ho ako zaklad
                if (!universal && (ac || dc)) {
                    setUniversalPlan({ ...(ac ?? dc)!, connectorType: null });
                }
            }
        })();
    }, [open, initialModel?.id, dispatch])

    useEffect(() => {
        if (!open) return;
        setTabIndex(0);
        setFlexiCharging(initialModel?.flexiCharging || false);
        setPreferFlexiCharging(initialModel?.preferFlexiCharging ?? true);
        setTenant(initialModel?.tenantId || '');
        setMarket(initialModel?.marketId || (marketsState?.value?.[0]?.id ?? ''));
        setLoadBalancers(initialModel?.loadBalancers || []);
        setChargers(initialModel?.chargers || []);
        setBeforeSubmit(true);
    }, [open, initialModel, marketsState]);

    useEffect(() => {
        if (initialModel) {
            if (initialModel?.tenantId) {
                // editing existing → use initialModel
                setTenant(initialModel.tenantId as string);
            } else {
                // creating new → apply custom rule
                if (tenantId === mainTenantId) {
                    setTenant(chargeTenantId);
                } else {
                    setTenant(tenantId);
                }
            }
            if (initialModel?.marketId) {
                // editing existing → use initialModel
                setMarket(initialModel.marketId as string);
            } else if (marketsState && marketsState?.value?.length > 0) {
                setMarket(marketsState.value[0].id as string);
            }
            setFlexiCharging(initialModel.flexiCharging)
            setPreferFlexiCharging(initialModel.preferFlexiCharging ?? true)
        }
    }, [open, initialModel, marketsState, tenantId])

    return (
        <Dialog className={classes.dialog} open={open}>
            <DialogProgress stateSelector={(state) => state.locations.edit} />
            <DialogTitle className={classes.dialogTitle}>{"Flexi Charging Setup"}</DialogTitle>

            <Form
                onSubmit={handleSubmit}
                initialValues={initialModel}
                validate={() => {
                    const errors: Record<string, any> = {};

                    loadBalancers.forEach(lb => {
                        if (!lb.name?.trim()) {
                            errors[lb.id] = { name: 'Name is required' };
                        }
                        if (lb.capacityKw == null || lb.capacityKw <= 0) {
                            errors[lb.id] = { ...errors[lb.id], capacityKw: 'Capacity is required' };
                        }
                    });

                    const validateHierarchy = (parentId: string | null, parentCapacity: number) => {
                        // Nájdeme všetky priame deti tohto rodiča
                        const children = loadBalancers.filter(lb => lb.parentLbId === parentId);

                        // Vypočítame sumu kapacít detí
                        const childrenTotalCapacity = children.reduce((sum, child) => sum + (Number(child.capacityKw) || 0), 0);

                        // Ak suma detí prekročí kapacitu rodiča, označíme všetky tieto deti chybou
                        if (parentId !== null && childrenTotalCapacity > parentCapacity) {
                            children.forEach(child => {
                                errors[child.id] = {
                                    ...errors[child.id],
                                    capacityKw: `Sum of siblings (${childrenTotalCapacity}kW) exceeds parent capacity (${parentCapacity}kW)`
                                };
                            });
                        }

                        // Rekurzia: Skontrolujeme deti týchto detí
                        children.forEach(child => validateHierarchy(child.id, child.capacityKw));
                    };

                    // Spustíme validáciu od koreňových elementov
                    // Predpokladáme, že "hlavný prívod" (root) je limitovaný nejakou externou hodnotou alebo 
                    // proste len kontrolujeme úrovne pod ním.
                    const roots = loadBalancers.filter(lb => !lb.parentLbId);
                    roots.forEach(root => validateHierarchy(root.id, root.capacityKw));

                    return { loadBalancers: errors };
                }}


                render={({ handleSubmit, form, values, errors }) => (
                    <form onSubmit={handleSubmit} noValidate className={classes.form}>
                        <DialogContent className={classes.dialogContent}>
                            <Tabs value={tabIndex} onChange={(event: any, newValue: number) => setTabIndex(newValue)}>
                                <Tab label={t('locations.generalSettings')} />
                                <Tab label={t('locations.defaultFlexiPlan')} />
                            </Tabs>

                            <TabPanel value={tabIndex} index={0} padding={0}>
                                <Grid container item xs={12} spacing={4} alignItems="center" wrap="nowrap" style={{ marginTop: 16 }}>
                                    <Grid item>
                                        <Typography variant="caption" style={{ fontSize: '18px', display: 'flex', alignItems: 'center' }}>
                                            <Checkbox
                                                name="flexiCharging"
                                                checked={flexiCharging}
                                                onChange={handleFlexiChargingChange}
                                                disabled={tenantId !== mainTenantId}
                                                classes={{
                                                    root: classes.checkbox,
                                                    checked: classes.checked,
                                                    disabled: classes.disabled,
                                                }}
                                            />
                                            <span style={{ color: tenantId === mainTenantId ? '#000000' : '#A0A0A0' }}>
                                                {t('locations.flexiCharging')}
                                            </span>

                                            <Tooltip title='Flexi charging feature may be enabled/disabled only by SuperAdmin, please contact support if you want to change it.' arrow>
                                                <span style={{ display: 'inline-block', verticalAlign: 'middle', cursor: 'pointer' }}>
                                                    <IconButton
                                                        size="small"
                                                        style={{
                                                            marginLeft: 4,
                                                            bottom: 1,
                                                            pointerEvents: tenantId === mainTenantId ? 'auto' : 'none',
                                                        }}
                                                    >
                                                        <HelpOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Typography>
                                    </Grid>
                                    <Grid item className={!flexiCharging ? classes.disabledContainer : ''}>
                                        <Typography variant="caption" style={{ fontSize: '18px', display: 'flex', alignItems: 'center' }}>
                                            <Checkbox
                                                name="preferFlexiCharging"
                                                checked={preferFlexiCharging}
                                                onChange={handlePreferFlexiChargingChange}
                                                classes={{
                                                    root: classes.checkbox,
                                                    checked: classes.checked,
                                                    disabled: classes.disabled,
                                                }}
                                            />
                                            <span>
                                                {t('locations.preferFlexiCharging')}
                                            </span>

                                            <Tooltip title="When enabled, RFID charging at this location automatically prioritizes flexi charging (if available), even when other pricing conditions also apply. When disabled, flexi charging is only assigned if the user's last session at this location was also flexi." arrow>
                                                <span style={{ display: 'inline-block', verticalAlign: 'middle', cursor: 'pointer' }}>
                                                    <IconButton
                                                        size="small"
                                                        style={{
                                                            marginLeft: 4,
                                                            bottom: 1,
                                                        }}
                                                    >
                                                        <HelpOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <div className={!flexiCharging ? classes.disabledContainer : ''}>
                                <Grid item xs={12} className={classes.selectContainer} style={{ marginTop: 16 }}>
                                    <FormControl required variant="filled" fullWidth>
                                        <InputLabel >{t('locations.market')}</InputLabel>
                                        <Select value={market} onChange={handleMarketChange}>
                                            {marketsState.value.map((item: any) => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {item.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <LoadBalancerEditor
                                    loadBalancers={loadBalancers}
                                    chargers={chargers}
                                    onEdit={handleEditLb}
                                    onCreate={handleCreateLb}
                                    onDelete={handleDeleteLb}
                                    errors={errors?.loadBalancers ?? []}
                                />
                                </div>
                                </TabPanel>

                                <TabPanel value={tabIndex} index={1} padding={0}>
                                <div className={!flexiCharging ? classes.disabledContainer : ''}>
                                <Grid item xs={12} style={{ marginBottom: 24, marginTop: 16 }}>
                                    <Typography variant="caption" style={{ marginBottom: 8, display: 'block' }}>
                                        {t('locations.defaultFlexiPlanHelp')}
                                    </Typography>

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={defaultPlanSplit}
                                                onChange={(e) => handleDefaultPlanSplitToggle(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={t('locations.splitByConnectorType')}
                                    />

                                    {!defaultPlanSplit && (
                                        <Grid container spacing={2} style={{ marginTop: 4 }}>
                                            <Grid item xs={6}>
                                                <FormControl required variant="filled" fullWidth error={universalPlanErrors.maxAcPower}>
                                                    <InputLabel>{t('locations.maxAcPower')}</InputLabel>
                                                    <Select
                                                        value={universalPlan.maxAcPower ?? ''}
                                                        onChange={makePlanAcPowerHandler(setUniversalPlan)}
                                                    >
                                                        {AC_POWER_OPTIONS.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label={t('locations.maxDcPower')}
                                                    type="number"
                                                    variant="filled"
                                                    fullWidth
                                                    required
                                                    error={universalPlanErrors.maxDcPower}
                                                    helperText={universalPlanErrors.maxDcPower ? t('common.required') : undefined}
                                                    value={universalPlan.maxDcPower ?? ''}
                                                    onChange={makePlanNumberFieldHandler(setUniversalPlan, 'maxDcPower')}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label={t('locations.requestedKwh')}
                                                    type="number"
                                                    variant="filled"
                                                    fullWidth
                                                    required
                                                    error={universalPlanErrors.requestedKwh}
                                                    helperText={universalPlanErrors.requestedKwh ? t('common.required') : undefined}
                                                    value={universalPlan.requestedKwh ?? ''}
                                                    onChange={makePlanNumberFieldHandler(setUniversalPlan, 'requestedKwh')}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label={t('locations.durationHours')}
                                                    type="number"
                                                    variant="filled"
                                                    fullWidth
                                                    required
                                                    error={universalPlanErrors.durationHours}
                                                    helperText={universalPlanErrors.durationHours ? t('common.required') : undefined}
                                                    value={universalPlan.durationHours ?? ''}
                                                    onChange={makePlanNumberFieldHandler(setUniversalPlan, 'durationHours')}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    className={classes.overtimeCheckboxLabel}
                                                    control={
                                                        <Checkbox
                                                            checked={universalPlan.overtimeCharging}
                                                            onChange={makePlanOvertimeHandler(setUniversalPlan)}
                                                            classes={{ root: classes.checkbox, checked: classes.checked }}
                                                        />
                                                    }
                                                    label={t('locations.overtimeCharging')}
                                                />
                                            </Grid>
                                        </Grid>
                                    )}

                                    {defaultPlanSplit && (
                                        <Grid container spacing={2} alignItems="center" style={{ marginTop: 4 }}>
                                            <Grid item xs={12}>
                                                <Typography variant="caption" style={{ fontWeight: 600 }}>{t('locations.acPlan')}</Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <FormControl required variant="filled" fullWidth error={acPlanErrors.maxAcPower}>
                                                    <InputLabel>{t('locations.maxAcPower')}</InputLabel>
                                                    <Select
                                                        value={acPlan.maxAcPower ?? ''}
                                                        onChange={makePlanAcPowerHandler(setAcPlan)}
                                                    >
                                                        {AC_POWER_OPTIONS.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label={t('locations.requestedKwh')}
                                                    type="number"
                                                    variant="filled"
                                                    fullWidth
                                                    required
                                                    error={acPlanErrors.requestedKwh}
                                                    helperText={acPlanErrors.requestedKwh ? t('common.required') : undefined}
                                                    value={acPlan.requestedKwh ?? ''}
                                                    onChange={makePlanNumberFieldHandler(setAcPlan, 'requestedKwh')}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label={t('locations.durationHours')}
                                                    type="number"
                                                    variant="filled"
                                                    fullWidth
                                                    required
                                                    error={acPlanErrors.durationHours}
                                                    helperText={acPlanErrors.durationHours ? t('common.required') : undefined}
                                                    value={acPlan.durationHours ?? ''}
                                                    onChange={makePlanNumberFieldHandler(setAcPlan, 'durationHours')}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <FormControlLabel
                                                    className={classes.overtimeCheckboxLabel}
                                                    control={
                                                        <Checkbox
                                                            checked={acPlan.overtimeCharging}
                                                            onChange={makePlanOvertimeHandler(setAcPlan)}
                                                            classes={{ root: classes.checkbox, checked: classes.checked }}
                                                        />
                                                    }
                                                    label={t('locations.overtimeCharging')}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography variant="caption" style={{ fontWeight: 600 }}>{t('locations.dcPlan')}</Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label={t('locations.maxDcPower')}
                                                    type="number"
                                                    variant="filled"
                                                    fullWidth
                                                    required
                                                    error={dcPlanErrors.maxDcPower}
                                                    helperText={dcPlanErrors.maxDcPower ? t('common.required') : undefined}
                                                    value={dcPlan.maxDcPower ?? ''}
                                                    onChange={makePlanNumberFieldHandler(setDcPlan, 'maxDcPower')}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label={t('locations.requestedKwh')}
                                                    type="number"
                                                    variant="filled"
                                                    fullWidth
                                                    required
                                                    error={dcPlanErrors.requestedKwh}
                                                    helperText={dcPlanErrors.requestedKwh ? t('common.required') : undefined}
                                                    value={dcPlan.requestedKwh ?? ''}
                                                    onChange={makePlanNumberFieldHandler(setDcPlan, 'requestedKwh')}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label={t('locations.durationHours')}
                                                    type="number"
                                                    variant="filled"
                                                    fullWidth
                                                    required
                                                    error={dcPlanErrors.durationHours}
                                                    helperText={dcPlanErrors.durationHours ? t('common.required') : undefined}
                                                    value={dcPlan.durationHours ?? ''}
                                                    onChange={makePlanNumberFieldHandler(setDcPlan, 'durationHours')}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <FormControlLabel
                                                    className={classes.overtimeCheckboxLabel}
                                                    control={
                                                        <Checkbox
                                                            checked={dcPlan.overtimeCharging}
                                                            onChange={makePlanOvertimeHandler(setDcPlan)}
                                                            classes={{ root: classes.checkbox, checked: classes.checked }}
                                                        />
                                                    }
                                                    label={t('locations.overtimeCharging')}
                                                />
                                            </Grid>
                                        </Grid>
                                    )}
                                </Grid>
                                </div>
                                </TabPanel>
                        </DialogContent>
                        <DialogActions className={classes.dialogActions}>
                            <Button
                                onClick={() => {
                                    onDialogClose()
                                }}
                                color="primary"
                            >
                                {t('common.cancel')}
                            </Button>
                            <DialogConfirmButton
                                label={t('common.change')}
                                stateSelector={(state) => state.locations.edit}
                                onClick={() => {
                                    // setValidationActive(true);
                                    if (beforeSubmit) {
                                        setBeforeSubmit(false)
                                    }
                                }}
                            />
                        </DialogActions>
                    </form>
                )}
            />
        </Dialog>
    )
}

export default FlexiChargingSetupDialog
